import React, { useCallback, useContext } from "react";
import { connect } from "react-redux";

import { Dispatch, bindActionCreators } from "redux";

import { ActionBar, Sticky } from "@plex/react-components";

import { INotification, notification } from "infrastructure/notification";
import { DialogNavigationContext, ActionBarLink } from "ui/app/navigation";
import { useLocalizedValues, useUserContext } from "ui/hooks";
import { displayMessages } from "ui/screens/translation-managers/translation-managers.localization";

import { messagesActions } from "../Messages.actions";
import { IMessagesState, IMessagesSearchQuery } from "../Messages.state";
import { MessagesUpload, MessagesUploadResult } from "../upload";

export interface IMessagesActionBarProps {
  state: IMessagesState;
  downloadMessagesUploadTemplate: () => void;
  onMessagesUploadCompleted?: (notification: INotification) => void;
  isExportDisabled?: boolean;
  exportToXml: (searchQuery: IMessagesSearchQuery) => void;
}

export const MessagesActionBar: React.FC<IMessagesActionBarProps> = ({ state, downloadMessagesUploadTemplate, onMessagesUploadCompleted, isExportDisabled, exportToXml }) => {
  const localizedValues = useLocalizedValues(() => ({
    messagesUploadTitle: displayMessages.title("messagesUpload"),
    messageAddAction: displayMessages.action("messageAdd"),
    messageUpdateAction: displayMessages.action("messageUpdate"),
    messagesUploadAction: displayMessages.action("messagesUpload"),
    selectMessagesUploadFileAction: displayMessages.action("selectMessagesUploadFile"),
    exportMessagesAction: displayMessages.action("exportMessages"),
    downloadMessagesFileTemplateAction: displayMessages.action("downloadMessagesFileTemplate"),
    allTranslationAction: displayMessages.action("allTranslations")
  }));

  const dialogNavigationContext = useContext(DialogNavigationContext);
  const userContext = useUserContext();
  const roles = userContext.getRoleAccessData();

  const exportExecute = useCallback(
    searchQuery => {
      exportToXml && exportToXml(searchQuery);
    },
    [exportToXml]
  );

  const downloadXmlTemplateExecute = useCallback(() => {
    downloadMessagesUploadTemplate && downloadMessagesUploadTemplate();
  }, [downloadMessagesUploadTemplate]);

  return (
    <Sticky>
      <ActionBar>
        {roles.PlexTechnicalWriter && <ActionBarLink text={localizedValues.messageAddAction} title={localizedValues.messageAddAction} href={"/message-details"} />}
        {roles.PlexTechnicalWriter && (
          <ActionBarLink
            text={localizedValues.messageUpdateAction}
            title={localizedValues.messageUpdateAction}
            href={`/message-details/${state.grid.selected}`}
            disabled={!state.grid.selected}
          />
        )}

        {roles.PlexTechnicalWriter && (
          <ActionBar.Dropdown>
            <ActionBar.DropdownButton text={localizedValues.messagesUploadAction} title={localizedValues.messagesUploadTitle} />
            <ActionBar.DropdownItems>
              <ActionBarLink
                text={localizedValues.selectMessagesUploadFileAction}
                title={localizedValues.selectMessagesUploadFileAction}
                dialog={{
                  content: (
                    <MessagesUpload
                      onUploadCompleted={error => {
                        if (onMessagesUploadCompleted) {
                          if (error) {
                            dialogNavigationContext.showDialog({
                              content: <MessagesUploadResult />
                            });
                          } else {
                            onMessagesUploadCompleted(notification.success().useMessage({ messageId: displayMessages.info("messagesUploadCompleted") }));
                          }
                        }
                      }}
                    />
                  )
                }}
              />
              <ActionBar.Divider />
              <ActionBar.Button
                text={localizedValues.downloadMessagesFileTemplateAction}
                title={localizedValues.downloadMessagesFileTemplateAction}
                onClick={downloadXmlTemplateExecute}
              />
            </ActionBar.DropdownItems>
          </ActionBar.Dropdown>
        )}
        <ActionBar.Button
          text={localizedValues.exportMessagesAction}
          title={localizedValues.exportMessagesAction}
          onClick={() => exportExecute(state.searchQuery)}
          disabled={isExportDisabled}
        />

        <ActionBarLink
          openInDialog
          disabled={!state.grid.selected}
          text={localizedValues.allTranslationAction}
          href={`/messages/${state.grid.selected}/translations`}
          title={localizedValues.allTranslationAction}
        />
      </ActionBar>
    </Sticky>
  );
};

export default connect(
  (state: { messages: IMessagesState }) => {
    return {
      state: state.messages,
      isExportDisabled: state.messages.isExportDisabled
    };
  },
  (dispatch: Dispatch) => {
    return bindActionCreators(
      {
        exportToXml: messagesActions.exportMessagesToXml,
        onMessagesUploadCompleted: messagesActions.onMessagesUploadCompleted,
        downloadMessagesUploadTemplate: messagesActions.downloadMessagesUploadTemplate
      },
      dispatch
    );
  }
)(MessagesActionBar);
